import React, {Component} from 'react';
import {passCsrfToken} from '../../helpers';
import axios from 'axios';
import history from '../../history';
import {Link} from 'react-router-dom';
import {Segment, Pagination} from 'semantic-ui-react';
import queryString from 'query-string';
import moment from 'moment';
import {getServiceTransactions} from '../../api/serviceTransactionApi';
import {getAllPartners} from '../../api/partnerApi';
import {formatMoneyLegacy} from '../../utils/currency';
import {
	txDescription,
	sortLedgerAsc,
	balanceAfterForTransaction,
	ledgerTotals,
} from '../../utils/vendorLedger';

function vendorLabel(partner) {
	if (!partner) return 'Vendor';
	const name = [partner.first_name, partner.last_name].filter(Boolean).join(' ')
		|| partner.company_name
		|| partner.email
		|| 'Vendor';
	return partner.company_name && name !== partner.company_name
		? `${name} (${partner.company_name})`
		: name;
}

class ServiceTransactions extends Component {
	constructor(props) {
		super(props);
		const qs = queryString.parse(props.location?.search || '');
		this.state = {
			serviceTransactions: [],
			pagination: {},
			partners: [],
			selectedVendorId: qs.vendor_id ? String(qs.vendor_id) : '',
			directionFilter: qs.direction || '',
			loading: false,
		};
	}

	componentDidMount() {
		passCsrfToken(document, axios);
		this.loadPartners();
		if (this.state.selectedVendorId) {
			this.fetchServiceTransaction(queryString.parse(this.props.location.search));
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location.search !== this.props.location.search) {
			const qs = queryString.parse(this.props.location.search || '');
			const selectedVendorId = qs.vendor_id ? String(qs.vendor_id) : '';
			const directionFilter = qs.direction || '';
			this.setState({ selectedVendorId, directionFilter }, () => {
				if (selectedVendorId) this.fetchServiceTransaction(qs);
				else this.setState({ serviceTransactions: [], pagination: {} });
			});
		}
	}

	loadPartners = () => {
		getAllPartners({ per_page: 500 })
			.then((response) => {
				const data = response.data || {};
				const list = Array.isArray(data) ? data : (data.vendors || data.partners || []);
				this.setState({ partners: list });
			})
			.catch(() => {});
	};

	fetchServiceTransaction(params = {}) {
		const vendorId = params.vendor_id || this.state.selectedVendorId;
		if (!vendorId) return;

		const direction = params.direction !== undefined ? params.direction : this.state.directionFilter;
		this.setState({ loading: true });
		const query = {
			page: params.page || 1,
			per_page: params.per_page || 500,
			q: {
				vendor_id_eq: vendorId,
			},
		};
		if (direction) query.q.direction_eq = direction;

		getServiceTransactions(query)
			.then((response) => {
				this.setState({
					serviceTransactions: response.data.service_transactions || [],
					pagination: response.data.meta?.pagination || {},
					loading: false,
				});
			})
			.catch((error) => {
				console.log(' Transaction fetch error', error);
				this.setState({ loading: false });
			});
	}

	changeCurrentPage = (e, { activePage }) => {
		const qs = queryString.parse(this.props.location.search || {});
		const searchQuery = queryString.stringify({ ...qs, page: activePage });
		history.push({ pathname: window.location.pathname, search: `?${searchQuery}` });
		this.fetchServiceTransaction({ ...qs, page: activePage });
	};

	handleVendorChange = (e) => {
		const selectedVendorId = e.target.value;
		const search = selectedVendorId
			? `?${queryString.stringify({ vendor_id: selectedVendorId })}`
			: '';
		history.push({ pathname: window.location.pathname, search });
		this.setState(
			{ selectedVendorId, directionFilter: '', serviceTransactions: [] },
			() => {
				if (selectedVendorId) this.fetchServiceTransaction({ vendor_id: selectedVendorId });
			}
		);
	};

	handleDirectionFilter = (directionFilter) => {
		const qs = queryString.parse(this.props.location.search || {});
		const next = { ...qs, vendor_id: this.state.selectedVendorId };
		if (directionFilter) next.direction = directionFilter;
		else delete next.direction;
		history.push({ pathname: window.location.pathname, search: `?${queryString.stringify(next)}` });
		this.setState({ directionFilter }, () => {
			this.fetchServiceTransaction({
				vendor_id: this.state.selectedVendorId,
				direction: directionFilter,
			});
		});
	};

	render() {
		const {
			serviceTransactions,
			pagination,
			partners,
			selectedVendorId,
			directionFilter,
			loading,
		} = this.state;

		const selectedPartner = partners.find((p) => String(p.id) === String(selectedVendorId));
		const ledgerRows = sortLedgerAsc(serviceTransactions);
		const totals = ledgerTotals(ledgerRows);
		const displayCc = ledgerRows[ledgerRows.length - 1]?.currency_code
			|| ledgerRows[0]?.currency_code
			|| 'USD';
		const pag = pagination || {};
		const totalPages = pag.total_pages || 1;
		const currentPage = pag.current_page || 1;

		return (
			<div className="ui container admin-vendor-ledger">
				<div className="row justify-content-between align-items-start mb-3">
					<div className="col-md-8">
						<h3 className="title mb-1">Vendor ledger</h3>
						<p className="text-muted small mb-0">
							One statement per vendor — like a bank account. Pick a vendor below to see their credits, payouts, and running balance.
						</p>
					</div>
					<div className="col-md-4 text-md-right mt-2 mt-md-0">
						<button
							type="button"
							className="ui button primary"
							onClick={() => {
								const q = selectedVendorId ? `?vendor_id=${selectedVendorId}` : '';
								history.push(`/admin/service_transaction/new${q}`);
							}}
						>
							Record payout
						</button>
					</div>
				</div>

				<div className="admin-vendor-ledger__picker card border-0 shadow-sm mb-3">
					<div className="card-body py-3">
						<label htmlFor="vendor-ledger-select" className="font-weight-bold small text-uppercase text-muted mb-1">
							Select vendor
						</label>
						<select
							id="vendor-ledger-select"
							className="form-control form-control-lg"
							value={selectedVendorId}
							onChange={this.handleVendorChange}
						>
							<option value="">— Choose a vendor to view their ledger —</option>
							{partners.map((partner) => (
								<option key={partner.id} value={partner.id}>
									{vendorLabel(partner)}
								</option>
							))}
						</select>
						<p className="small text-muted mb-0 mt-2">
							Need amounts owed across vendors? Use{' '}
							<Link to="/admin/summaries">Vendor payouts</Link>.
						</p>
					</div>
				</div>

				{!selectedVendorId && (
					<Segment placeholder className="text-center py-5">
						<p className="text-muted mb-0">Select a vendor above to view their ledger entries.</p>
					</Segment>
				)}

				{selectedVendorId && (
					<>
						<div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
							<div>
								<h4 className="h6 mb-0">{vendorLabel(selectedPartner)}</h4>
								{ledgerRows.length > 0 && (
									<p className="small text-muted mb-0">
										This view: earned {formatMoneyLegacy(totals.credit, displayCc)}
										{' · '}paid {formatMoneyLegacy(totals.debit, displayCc)}
									</p>
								)}
							</div>
							<div className="btn-group btn-group-sm" role="group" aria-label="Filter by type">
								<button
									type="button"
									className={`btn btn-outline-secondary${!directionFilter ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter('')}
								>
									All
								</button>
								<button
									type="button"
									className={`btn btn-outline-secondary${directionFilter === 'credit' ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter('credit')}
								>
									Credits only
								</button>
								<button
									type="button"
									className={`btn btn-outline-secondary${directionFilter === 'debit' ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter('debit')}
								>
									Debits only
								</button>
							</div>
						</div>

						{loading ? (
							<p className="text-muted">Loading…</p>
						) : (
							<Segment className="p-0">
								{ledgerRows.length === 0 ? (
									<p className="text-muted p-3 mb-0">No entries for this vendor yet.</p>
								) : (
									<div className="table-responsive">
										<table className="table table-striped table-bordered mb-0">
											<thead>
												<tr>
													<th>#</th>
													<th>Date</th>
													<th>Description</th>
													<th>Payment mode</th>
													<th className="text-right">
														Debit
														<div className="small font-weight-normal text-muted">(you paid vendor)</div>
													</th>
													<th className="text-right">
														Credit
														<div className="small font-weight-normal text-muted">(you owe vendor)</div>
													</th>
													<th className="text-right">
														Balance after
														<div className="small font-weight-normal text-muted">(still owed)</div>
													</th>
													<th>Booking</th>
												</tr>
											</thead>
											<tbody>
												<tr className="table-light">
													<td colSpan={4}>
														<em>Opening balance</em>
													</td>
													<td className="text-right text-muted">—</td>
													<td className="text-right text-muted">—</td>
													<td className="text-right font-weight-bold">{formatMoneyLegacy(0, displayCc)}</td>
													<td />
												</tr>
												{ledgerRows.map((transaction, index) => {
													const cc = transaction.currency_code || displayCc;
													const balance = balanceAfterForTransaction(transaction, serviceTransactions);
													return (
														<tr key={transaction.idx || index}>
															<td>{index + 1}</td>
															<td>{moment(transaction.created_at).format('D MMM YYYY')}</td>
															<td>{txDescription(transaction)}</td>
															<td>{transaction.payment_mode || '—'}</td>
															<td className="text-right">
																{transaction.direction === 'debit'
																	? formatMoneyLegacy(transaction.amount, cc)
																	: ''}
															</td>
															<td className="text-right">
																{transaction.direction === 'credit'
																	? formatMoneyLegacy(transaction.amount, cc)
																	: ''}
															</td>
															<td className="text-right font-weight-bold">
																{formatMoneyLegacy(balance, cc)}
															</td>
															<td>
																{transaction.booking_details?.type === 'RENTAL' && (
																	<Link to={`/admin/car_bookings/${transaction.booking_details.idx}`}>
																		View
																	</Link>
																)}
																{transaction.booking_details?.type === 'PACKAGE' && (
																	<Link to={`/admin/package_booking_details/${transaction.booking_details.idx}`}>
																		View
																	</Link>
																)}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</Segment>
						)}

						{totalPages > 1 && (
							<div className="text-center p-2">
								<Pagination
									activePage={currentPage}
									totalPages={totalPages}
									onPageChange={this.changeCurrentPage}
								/>
							</div>
						)}
					</>
				)}
			</div>
		);
	}
}

export default ServiceTransactions;
